// Recherche globale (Ctrl K ou /) : clavier complet, résultats groupés, correspondance exacte signalée.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { api } from '../lib/api.ts';
import { Badge, Chargement, EtatErreur, Surligne } from '../components/ui.tsx';

interface Res { q: string; groupes: { type: string; libelle: string; resultats: { id: string; titre: string; sousTitre?: string; to: string; extrait?: string; exact?: boolean }[] }[] }

export function RechercheGlobale({ ouvert, fermer }: { ouvert: boolean; fermer: () => void }) {
  const [q, setQ] = useState('');
  const [qDiff, setQDiff] = useState('');
  const [actif, setActif] = useState(0);
  const ref = useRef<HTMLInputElement>(null);
  const nav = useNavigate();

  useEffect(() => { const t = setTimeout(() => setQDiff(q.trim()), 220); return () => clearTimeout(t); }, [q]);
  useEffect(() => { if (ouvert) { setTimeout(() => ref.current?.focus(), 10); } else { setQ(''); setQDiff(''); } }, [ouvert]);

  const r = useQuery({
    queryKey: ['recherche', qDiff], enabled: ouvert && qDiff.length >= 2,
    queryFn: ({ signal }) => api.get<Res>(`/api/recherche?q=${encodeURIComponent(qDiff)}`, signal),
  });
  const plats = (r.data?.groupes ?? []).flatMap((g) => g.resultats);
  useEffect(() => setActif(0), [r.data]);

  if (!ouvert) return null;
  const aller = (to: string) => { fermer(); nav(to); };

  return (
    <div className="voile" onMouseDown={(e) => { if (e.target === e.currentTarget) fermer(); }}>
      <div className="dialogue dialogue-large" role="dialog" aria-modal="true" aria-label="Recherche globale"
        onKeyDown={(e) => {
          if (e.key === 'Escape') fermer();
          if (e.key === 'ArrowDown') { e.preventDefault(); setActif((a) => Math.min(a + 1, plats.length - 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setActif((a) => Math.max(a - 1, 0)); }
          if (e.key === 'Enter' && plats[actif]) { e.preventDefault(); aller(plats[actif].to); }
        }}>
        <div className="dialogue-entete" style={{ gap: 8 }}>
          <Search size={20} aria-hidden style={{ color: 'var(--encre-3)', flex: 'none' }} />
          <input ref={ref} value={q} onChange={(e) => setQ(e.target.value)} className="saisie" style={{ border: 0, outline: 'none', fontSize: 'var(--t-l)', minHeight: 44 }}
            placeholder="Référence exacte, code défaut, marque, équipement, site, document…" aria-label="Rechercher"
            role="combobox" aria-expanded={plats.length > 0} aria-controls="resultats-recherche" aria-activedescendant={plats[actif] ? `res-${actif}` : undefined} />
          <button type="button" className="btn btn-fantome btn-icone" onClick={fermer} aria-label="Fermer la recherche"><X aria-hidden /></button>
        </div>
        <div className="dialogue-corps" style={{ padding: 0 }} id="resultats-recherche" role="listbox">
          {qDiff.length < 2 && <p className="discret" style={{ padding: 20 }}>Saisissez au moins deux caractères. Une référence exacte (ex. <span className="mono">SXWIPBS1</span>) ou un code défaut est mis en tête.</p>}
          {qDiff.length >= 2 && r.isLoading && <Chargement lignes={4} />}
          {r.isError && <EtatErreur erreur={r.error} reessayer={() => r.refetch()} />}
          {r.data && !plats.length && <p style={{ padding: 20 }}>Aucun résultat pour « {r.data.q} ». Vérifiez l’orthographe de la référence, ou importez la documentation correspondante.</p>}
          {(() => {
            let i = -1;
            return r.data?.groupes.map((g) => (
              <div key={g.type}>
                <div className="tres-petit discret" style={{ padding: '10px 20px 4px', fontWeight: 600 }}>{g.libelle}</div>
                {g.resultats.map((x) => {
                  i++; const idx = i;
                  return (
                    <div key={x.id} id={`res-${idx}`} role="option" aria-selected={idx === actif} onMouseEnter={() => setActif(idx)} onClick={() => aller(x.to)}
                      className="liste-element" style={{ cursor: 'pointer', background: idx === actif ? 'var(--accent-doux)' : undefined, minHeight: 56 }}>
                      <div className="liste-element-corps">
                        <span className="liste-element-titre">{x.titre} {x.exact && <Badge ton="accent">Référence exacte</Badge>}</span>
                        {x.sousTitre && <span className="petit discret">{x.sousTitre}</span>}
                        {x.extrait && <span className="petit secondaire"><Surligne texte={x.extrait} /></span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ));
          })()}
        </div>
      </div>
    </div>
  );
}
